import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import LoadingScreen from 'src/Loading/LoadingScreen';
import { getUserInfo, getUserRolePermissions } from '../services/userServicer';
import { getV2Token } from '../utils/v2AuthUtils';
import { isDemoUser } from '../utils/demoAuth';
import { useTenant } from '../context/TenantContext';
import DashboardImplemetor from '../pages/DashboardImplementor/DashboardImplemetor';
import Dashboard from '../pages/dashboard/Dashboard';
import DemoDashboard from '../pages/demo/DemoDashboard';

function isPartnerToken(token: string | null): boolean {
  if (!token) return false;
  try {
    const decoded: any = jwtDecode(token);
    return decoded?.archetype === 'consulting_firm' || decoded?.archetype === 'audit_firm';
  } catch {
    return false;
  }
}

interface RoleBasedRouteProps {
  children?: React.ReactNode;
}

const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({ children }) => {
  const { t } = useTranslation();
  const { currentTenant } = useTenant();
  const tenantArchetype = currentTenant?.archetype ?? (currentTenant as any)?.rootEntityType ?? null;
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);
  const [notAllowed, setNotAllowed] = useState(false);

  useEffect(() => {
    const token = getV2Token();
    if (token && isDemoUser(token)) {
      setIsDemo(true);
      setIsLoading(false);
      return;
    }

    // Partner-archetype tenants land on the Partner Portal home, not /.
    // Short-circuit before hitting the admin permission gate below — partner
    // users have no /dashboard permission, so without this they would see
    // "Not allowed" before the Navigate fires.
    if (isPartnerToken(token)) {
      setIsLoading(false);
      return;
    }

    const getUserRole = async () => {
      try {
        setIsLoading(true);
        const userData = await getUserInfo();
        const userId = userData?.id;
        if (!userId) {
          setNotAllowed(true);
          setUserRole(null);
          return;
        }

        // Permission gate for dashboard (root "/")
        const permsResp = await getUserRolePermissions(userId);
        const dashboardPerm = permsResp?.success
          ? permsResp?.data?.permissions?.find(
              (p: any) => String(p?.page || '').toLowerCase() === 'dashboard',
            )
          : null;

        if (!dashboardPerm?.show) {
          setNotAllowed(true);
          setUserRole(null);
          return;
        }

        setNotAllowed(false);
        setUserRole(userData?.group?.name ?? userData?.groupName ?? null);
      } catch (err) {
        console.error("Error getting user role:", err);
        setUserRole(null);
        setNotAllowed(true);
      } finally {
        setIsLoading(false);
      }
    };

    getUserRole();
  }, []);

  // Client tenants are TrustLens-only — their home is the TrustLens Overview
  // (first page of the first menu), not the legacy compliance dashboard.
  if (!children && tenantArchetype === 'client') {
    return <Navigate to="/trustlens/dashboard" replace />;
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isPartnerToken(getV2Token())) {
    return <Navigate to="/partner-portal" replace />;
  }

  if (isDemo) {
    return <DemoDashboard />;
  }

  if (notAllowed) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: '70vh', padding: '2rem' }}
      >
        <h4 className="mb-2 text-center" style={{ color: '#111827' }}>
          {t('permissions.not_allowed') || "You don't have permission to see this page"}
        </h4>
        <div className="text-muted text-center" style={{ maxWidth: 680 }}>
          {t('Sidebar.Home') || 'Dashboard'}
        </div>
      </div>
    );
  }

  // If children are provided, render them (for custom role-based components)
  if (children) {
    return <>{children}</>;
  }

  // Default dashboard routing based on role
  if (userRole === 'Compliance_Implementor') {
    return <DashboardImplemetor />;
  }

  return <Dashboard />;
};

export default RoleBasedRoute;
